import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CalendarClock, X } from 'lucide-react';
import { Card } from 'components/ui/card';
import { DataTable } from 'components/DataTable';
import { EmptyState } from 'components/EmptyState';
import { apiErrorMessage } from 'utils/errorMessage';
import type { AdminIssue } from 'api/adminIssuesApi';
import { useMissions } from '../useMissions';
import MissionAssignmentPanel from './MissionAssignmentPanel';
import { getUpcomingColumns } from './upcomingColumns';
import { summarize } from './upcomingAssignment';
import { IssuesTableSkeleton } from './IssuesTableSkeleton';

/**
 * The « À venir » tab: the desk's missions, and where each one stands on being assigned.
 *
 * The missions come from the Jira-backed listing the other tab already loads — same filters, same
 * cursor pagination, same desk gate — so this component receives them rather than fetching them.
 * What it fetches is the other half: `intervention_requests` and `issue_specialties` for the keys
 * on screen, in one call.
 */

/** `DataTable`'s own `serverPagination` prop, forwarded whole — the tab shares the other's cursor. */
interface ServerPagination {
  pageIndex: number;
  pageCount: number | null;
  totalCount?: number | null;
  hasNextPage: boolean;
  onPreviousPage: () => void;
  onNextPage: () => void;
}

interface UpcomingMissionsTabProps {
  serviceDeskId: number;
  issues: AdminIssue[];
  isLoading: boolean;
  /** The store names are still in flight; the column greys instead of claiming no store. */
  storeLabelsLoading?: boolean;
  error: Error | null;
  canManage: boolean;
  /**
   * The desk writes the qualification workflow into Jira (D17). **Off by default**, since the flag
   * ships OFF: nothing on this screen then transitions anything, and no copy may claim it does.
   */
  hasInterventionQualification?: boolean;
  jiraBrowseBase?: string | null;
  serverPagination: ServerPagination;
}

const UpcomingMissionsTab = ({
  serviceDeskId,
  issues,
  isLoading,
  storeLabelsLoading = false,
  error,
  canManage,
  hasInterventionQualification = false,
  jiraBrowseBase,
  serverPagination,
}: UpcomingMissionsTabProps) => {
  const { t } = useTranslation();
  // The **key**, never the row: a released mission has to come back out of `missions` without its
  // technician, and a stored object would keep showing the card the release just cancelled.
  /**
   * `?assign=<key>` opens the drawer on arrival — the follow-up screen's Actions column links
   * here, because reassigning is what a row there is about (B-06/D36) and the ticket's detail page
   * cannot do it.
   *
   * The URL is read once into state rather than driven from it: the drawer is a view, and keeping
   * it addressable *while open* would push a history entry on every close. The parameter is
   * dropped as soon as it is consumed, so a reload does not reopen what somebody just closed.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedKey = searchParams.get('assign');
  const [assigningKey, setAssigningKey] = useState<string | null>(requestedKey);

  useEffect(() => {
    if (!requestedKey) return;
    setAssigningKey(requestedKey);
    const next = new URLSearchParams(searchParams);
    next.delete('assign');
    setSearchParams(next, { replace: true });
  }, [requestedKey, searchParams, setSearchParams]);
  const [jiraWarning, setJiraWarning] = useState<string | null>(null);

  const { missions, assignmentsKnown, error: assignmentsError } = useMissions(serviceDeskId, issues);
  const summary = useMemo(() => summarize(missions), [missions]);
  const assigning = missions.find((mission) => mission.issueKey === assigningKey) ?? null;
  /**
   * A deep link can name a mission this tab does not list: the tab shows one status and one page,
   * and a sent mission leaves it the day `hasInterventionQualification` transitions it (D17).
   * Silence would be a link that opens nothing — so it says which ticket, and where to read it.
   */
  const assignMissing = !!assigningKey && !assigning && !isLoading;

  const columns = useMemo(
    () =>
      getUpcomingColumns({
        t,
        canManage,
        onOpen: (mission) => setAssigningKey(mission.issueKey),
        jiraBrowseBase,
        selectedKey: assigningKey,
        assignmentsKnown,
        storeLabelsLoading,
      }),
    [t, canManage, jiraBrowseBase, assigningKey, assignmentsKnown, storeLabelsLoading]
  );

  return (
    <div className="space-y-4">
      {/* One card frames the listing: the summary line is its header, not a floating paragraph. */}
      <Card className="overflow-hidden rounded-[14px] shadow-[0_1px_2px_rgba(16,24,40,.04)]">
        {/*
          The four figures are the page's: Jira counts the missions, Postgres the states. The last
          three are **dropped** while the Postgres half is unknown rather than annotated: a figure
          with a caveat beside it is still read as a figure, and « 0 sans technicien » on a failed
          read is the same lie the rows were making. The mission count is Jira's and stays.
        */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-[14px]">
          <p className="text-[13px] text-muted-foreground" role="status">
            <span className="font-medium text-foreground">
              {t('{{count}} mission on this page', { count: summary.planned })}
            </span>
            {assignmentsKnown && (
              <>
                {' · '}
                {t('{{count}} with no technician', { count: summary.unassigned })}
                {' · '}
                {t('{{count}} awaiting an answer', { count: summary.pending })}
                {' · '}
                {t('{{count}} confirmed', { count: summary.accepted })}
              </>
            )}
          </p>
        </div>

        {/* Must survive a glance elsewhere. Amber and not rose — nothing was lost. */}
        {assignMissing && (
          <div
            className="flex items-start gap-2 border-b bg-amber-50 px-5 py-3 text-[13px] text-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
            role="alert"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="flex-1">
              {t('{{issueKey}} is not being qualified, so it cannot be assigned from here.', {
                issueKey: assigningKey,
              })}{' '}
              <Link to={`/admin/issues/${assigningKey}`} className="font-medium underline">
                {t('Open the ticket')}
              </Link>
            </span>
            <button
              type="button"
              onClick={() => setAssigningKey(null)}
              aria-label={t('Dismiss')}
              className="shrink-0 rounded p-0.5 text-amber-700 hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-100"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        )}

        {jiraWarning && (
          <div
            className="flex items-start gap-2 border-b bg-amber-50 px-5 py-3 text-[13px] text-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
            role="alert"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="flex-1">{jiraWarning}</span>
            <button
              type="button"
              onClick={() => setJiraWarning(null)}
              aria-label={t('Dismiss')}
              className="shrink-0 rounded p-0.5 text-amber-700 hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-100"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        )}

        {assignmentsError && (
          <p className="px-5 pt-3 text-[13px] text-rose-600 dark:text-rose-400" role="alert">
            {apiErrorMessage(t, assignmentsError)}
          </p>
        )}

        {isLoading ? (
          <IssuesTableSkeleton />
        ) : error ? (
          // `role="alert"` like every other error on this screen: a load that failed is the one a
          // reader must not have to discover by finding the table empty.
          <p className="px-5 py-4 text-sm text-red-600 dark:text-red-400" role="alert">
            {error.message}
          </p>
        ) : (
          /*
            No horizontal padding here: the mock-up runs the table the full width of the card and
            gets its inset from the cells' own `pl-5`/`pr-[18px]`. Wrapping it in `px-5` spent 40 px
            of a table that is already 46 px wider than the card at 1440.
          */
          <div className="pb-1 pt-3">
            <DataTable
              data={missions}
              columns={columns}
              embedded
              // Kept, unlike the technician directory this card's shape was copied from: that
              // screen has its own search in its filter bar, this tab does not. Removing it was a
              // regression of the visual pass. ⚠️ It filters the loaded page, not the result set —
              // the filter bar above is what searches the whole desk.
              searchPlaceholder={t('Filter this page…')}
              // An icon, a sentence, and what to do about it — a bare "no mission" cannot tell a
              // filter that is too narrow from a desk that genuinely has nothing to assign.
              emptyMessage={
                <EmptyState
                  icon={CalendarClock}
                  title={t('No mission to assign')}
                  hint={t('Adjust the filters above to see other missions.')}
                />
              }
              serverPagination={serverPagination}
            />
          </div>
        )}
      </Card>

      <MissionAssignmentPanel
        mission={assigning}
        serviceDeskId={serviceDeskId}
        hasInterventionQualification={hasInterventionQualification}
        onClose={() => setAssigningKey(null)}
        onJiraWarning={setJiraWarning}
      />
    </div>
  );
};

export default UpcomingMissionsTab;
